/**
 * WordPress dependencies
 */
import { store, getContext, getServerState } from '@wordpress/interactivity';

const { state, actions, callbacks } = store('lana/scroller', {
  state: {
    get isOpen() {
      return !!state.selectedPost.slug;
    },
    get isActiveShow() {
      const slug = getContext()?.slug;
      return slug === state.activeShow ? true : false;
    },
    get isLoadMoreBtnHidden() {
      const totalPosts = getServerState()?.totalPosts;
      return state.perPage >= totalPosts ? true : false;
    },
  },
  actions: {
    *filterShow(event) {
      event.preventDefault();
      window.lanaStreamPlayers = {};
      // Reset perPage to prevent large queries
      state.perPage = state.initialPerPage;
      const { actions: router } = yield import('@wordpress/interactivity-router');

      const context = getContext();
      if (!context) return;

      if (context?.slug === state.activeShow) {
        yield router.navigate(`${state.baseUrl}`, { replace: true });
        state.activeShow = '';
        return;
      }

      const url = new URL(event.target.href);
      url.searchParams.set('show', context?.slug);
      url.searchParams.set('perPage', state.perPage);

      yield router.navigate(url.toString(), { replace: true });
      window.history.replaceState(null, '', `${state.baseUrl}`);

      state.activeShow = context?.slug;
    },
    /**
     * Increases perPage each time Load More is clicked
     * Utilises interactivity-router to make the query re-render
     * taking on the new perPage parameter
     *
     * @param {Event} event - Click event triggered by Load More button
     */
    *navigate(event) {
      if (event) {
        event.preventDefault();
      }

      const { actions: router } = yield import('@wordpress/interactivity-router');

      state.perPage += state.loadMoreValue;

      const url = new URL(event ? event.target.href : state.baseUrl);
      const initialOpenSlug = getServerState()?.initialOpenSlug;

      if (initialOpenSlug) {
        url.searchParams.set('program_slug', initialOpenSlug);
      }

      url.searchParams.set('perPage', state.perPage);
      url.searchParams.set('show', state.activeShow);

      yield router.navigate(url.toString(), { replace: true });

      if (event) {
        window.history.replaceState(null, '', `${state.baseUrl}`);
      } else {
        window.history.replaceState(null, '', `${state.baseUrl}${state.selectedPost.slug}`);
      }
    },
    /**
     * Scrolls to previous post within the query
     */
    scrollUp() {
      requestAnimationFrame(() => {
        if (!state.selectedPost.id) {
          return;
        }

        const currentPost = document.getElementById(
          `program-${state?.selectedPost.id}${state?.selectedPost.index}`,
        );

        if (!currentPost) {
          return;
        }

        const previousPost = currentPost.previousElementSibling;
        if (!previousPost) {
          return;
        }

        previousPost.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      });
    },
    /**
     * Scrolls to next post within query
     */
    scrollDown() {
      requestAnimationFrame(() => {
        if (!state.selectedPost.id) {
          return;
        }

        const currentPost = document.getElementById(
          `program-${state?.selectedPost.id}${state?.selectedPost.index}`,
        );

        if (!currentPost) {
          return;
        }

        const nextPost = currentPost.nextElementSibling;

        if (!nextPost) {
          return;
        }

        nextPost.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      });
    },
    /**
     * Closes the popup overlay
     */
    closePopup() {
      const player = callbacks.getPlayer(`${state.selectedPost.id}${state.selectedPost.index}`);
      player.pause();
      window.lanaStreamPlayers = {};
      state.selectedPost = { id: null, slug: null, index: null };
    },
    /**
     * Opens the popup overlay
     */
    openPopup() {
      const context = getContext();
      state.selectedPost = { id: context?.id, slug: context?.slug, index: context?.index };
      requestAnimationFrame(() => {
        const targetPost = document.getElementById(`program-${context.id}${context.index}`);

        targetPost.scrollIntoView({
          block: 'start',
          inline: 'nearest',
        });
      });
    },
  },
  callbacks: {
    fetchMorePosts() {
      const perPage = state.perPage ? state.perPage : state.initialPerPage;
      if (state?.selectedPost?.index >= perPage - 3) {
        actions.navigate();
      }
    },
    scrollToInitialPost() {
      if (state?.selectedPost?.slug) {
        requestAnimationFrame(() => {
          const targetPost = document.getElementById(
            `program-${state?.selectedPost.id}${state?.selectedPost.index}`,
          );
          targetPost.scrollIntoView({
            block: 'start',
            inline: 'nearest',
          });
        });
      }
    },
    updateUrl() {
      if (!state?.selectedPost?.slug) {
        window.history.replaceState({}, '', `${state.baseUrl}`);
      } else {
        window.history.replaceState({}, '', `${state.baseUrl}${state?.selectedPost?.slug}`);
      }
    },
    closePopupEscKey() {
      document.addEventListener('keyup', (e) => {
        if (e.key !== 'Escape') {
          return;
        }
        actions.closePopup();
      });
    },
  },
});
